export type SeasonPhase =
  | "PRESEASON"
  | "REGULAR_SEASON"
  | "PLAYOFF_CUTOVER"
  | "PLAYOFFS"
  | "COMPLETED";

export type QualificationStatus = "PENDING" | "QUALIFIED" | "NOT_QUALIFIED";

export interface RegularSeasonRecord {
  games_played: number;
  wins: number;
  losses: number;
  ties: number;
  overtime_losses: number;
}

export type RecordPillLabel = "W" | "L" | "OTL" | "T";

export type RecordPill = {
  label: RecordPillLabel;
  value: number;
};

const RECORD_COMPONENTS: Record<string, ReadonlyArray<RecordPillLabel>> = {
  BASEBALL_TRADING: ["W", "L"],
  HOCKEY_TRADING: ["W", "L", "OTL"],
  FOOTBALL_TRADING: ["W", "L", "T"],
};

const RECORD_COMPETITION_ALIASES: Record<string, string> = {
  MLB_WORLD_SERIES: "BASEBALL_TRADING",
  NHL_STANLEY_CUP: "HOCKEY_TRADING",
  NFL_SUPERBOWL: "FOOTBALL_TRADING",
};

const RECORD_VALUE: Record<RecordPillLabel, keyof RegularSeasonRecord> = {
  W: "wins",
  L: "losses",
  OTL: "overtime_losses",
  T: "ties",
};

const RECORD_TRANSLATION_KEYS = {
  W: "market.record.wins",
  L: "market.record.losses",
  OTL: "market.record.overtimeLosses",
  T: "market.record.ties",
} as const;

export function recordPillTranslationKey(label: RecordPillLabel) {
  return RECORD_TRANSLATION_KEYS[label];
}

export function isRegularSeasonPhase(
  phase: SeasonPhase | string | null | undefined,
): phase is "PRESEASON" | "REGULAR_SEASON" {
  return phase === "PRESEASON" || phase === "REGULAR_SEASON";
}

export function buildRecordPills(
  competitionCode: string | null | undefined,
  record: RegularSeasonRecord | null | undefined,
  phase: SeasonPhase = "REGULAR_SEASON",
): RecordPill[] {
  if (!record || !isRegularSeasonPhase(phase)) return [];

  const normalizedCompetitionCode = (competitionCode ?? "").trim().toUpperCase();
  const labels =
    RECORD_COMPONENTS[
      RECORD_COMPETITION_ALIASES[normalizedCompetitionCode] ?? normalizedCompetitionCode
    ];
  if (!labels) return [];

  return labels.map((label) => ({ label, value: record[RECORD_VALUE[label]] }));
}

export type RegularSeasonMarketTeam = {
  team_code: string;
  team_name?: string | null;
  team_short_name?: string | null;
  conference_code: string | null;
  division_code: string | null;
  team_state: string;
  qualification_status: QualificationStatus;
};

export type RegularSeasonStatusFilter = "ALL" | "ACTIVE" | "ELIMINATED";

export type RegularSeasonTeamGroup<T extends RegularSeasonMarketTeam> = {
  key: string;
  conferenceCode: string;
  divisionCode: string;
  isFirstConferenceGroup: boolean;
  teams: T[];
};

type RegularSeasonGroupOptions = {
  searchQuery?: string;
  statusFilter?: RegularSeasonStatusFilter;
};

export function isMarketTeamEliminated(
  team: Pick<RegularSeasonMarketTeam, "qualification_status" | "team_state">,
): boolean {
  return (
    team.qualification_status === "NOT_QUALIFIED" ||
    team.team_state.trim().toUpperCase() === "ELIMINATED"
  );
}

export type MarketRowExpansionState = {
  isTradingClosed: boolean;
  effectiveExpanded: boolean;
  hookEnabled: boolean;
  ariaExpanded: boolean;
  panelVisible: boolean;
  controlLabel: "expand" | "collapse";
};

export type OrderTicketMarketState = {
  isTradingClosed: boolean;
  reasonKey: "market.tradingClosed.notQualified" | "market.tradingClosed.unavailable" | null;
};

export function resolveOrderTicketMarketState(
  team: Pick<RegularSeasonMarketTeam, "qualification_status" | "team_state"> | null | undefined,
): OrderTicketMarketState {
  if (!team) return { isTradingClosed: false, reasonKey: null };
  if (team.qualification_status === "NOT_QUALIFIED") {
    return {
      isTradingClosed: true,
      reasonKey: "market.tradingClosed.notQualified",
    };
  }
  const normalizedState = team.team_state.trim().toUpperCase();
  if (isMarketTeamEliminated(team) || ["CHAMPION", "CHAMP"].includes(normalizedState)) {
    return {
      isTradingClosed: true,
      reasonKey: "market.tradingClosed.unavailable",
    };
  }
  return { isTradingClosed: false, reasonKey: null };
}

export function resolveMarketRowExpansion({
  requestedExpanded,
  team,
}: {
  requestedExpanded: boolean;
  team: Pick<RegularSeasonMarketTeam, "qualification_status" | "team_state">;
}): MarketRowExpansionState {
  const normalizedState = team.team_state.trim().toUpperCase();
  const isChampion = normalizedState === "CHAMPION" || normalizedState === "CHAMP";
  const isTradingClosed = isMarketTeamEliminated(team) || isChampion;
  const effectiveExpanded = requestedExpanded && !isTradingClosed;

  return {
    isTradingClosed,
    effectiveExpanded,
    hookEnabled: effectiveExpanded,
    ariaExpanded: effectiveExpanded,
    panelVisible: effectiveExpanded,
    controlLabel: effectiveExpanded ? "collapse" : "expand",
  };
}

export function resolveMarketRoundText(
  currentRound: string | null,
  roundLabels: Readonly<Record<string, string>>,
  seasonPhase: SeasonPhase,
): string | null {
  if (isRegularSeasonPhase(seasonPhase)) return null;
  return currentRound ? roundLabels[currentRound] ?? currentRound : "-";
}

function matchesStatus(
  team: RegularSeasonMarketTeam,
  statusFilter: RegularSeasonStatusFilter,
): boolean {
  if (statusFilter === "ALL") return true;
  if (statusFilter === "ELIMINATED") return isMarketTeamEliminated(team);

  const state = team.team_state.trim().toUpperCase();
  return !isMarketTeamEliminated(team) && (state === "ACTIVE" || state === "PLAYING");
}

function matchesSearch(team: RegularSeasonMarketTeam, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  return [team.team_code, team.team_name, team.team_short_name]
    .filter((value): value is string => typeof value === "string")
    .some((value) => value.toLocaleUpperCase().includes(normalizedQuery));
}

export function groupRegularSeasonTeams<T extends RegularSeasonMarketTeam>(
  teams: readonly T[],
  { searchQuery = "", statusFilter = "ALL" }: RegularSeasonGroupOptions = {},
): Array<RegularSeasonTeamGroup<T>> {
  const normalizedQuery = searchQuery.trim().toLocaleUpperCase();
  const conferences = new Map<string, Map<string, RegularSeasonTeamGroup<T>>>();

  for (const team of teams) {
    if (!matchesStatus(team, statusFilter) || !matchesSearch(team, normalizedQuery)) continue;

    const conferenceCode = team.conference_code?.trim().toUpperCase() || "LEAGUE";
    const divisionCode = team.division_code?.trim().toUpperCase() || "OTHER";
    let divisions = conferences.get(conferenceCode);
    if (!divisions) {
      divisions = new Map<string, RegularSeasonTeamGroup<T>>();
      conferences.set(conferenceCode, divisions);
    }
    let group = divisions.get(divisionCode);
    if (!group) {
      group = {
        key: `${conferenceCode}:${divisionCode}`,
        conferenceCode,
        divisionCode,
        isFirstConferenceGroup: false,
        teams: [],
      };
      divisions.set(divisionCode, group);
    }
    group.teams.push(team);
  }

  return [...conferences.values()].flatMap((divisions) =>
    [...divisions.values()].map((group, index) => ({
      ...group,
      isFirstConferenceGroup: index === 0,
    })),
  );
}

const STRUCTURE_LABEL_KEYS = {
  AL: "market.structure.americanLeague",
  NL: "market.structure.nationalLeague",
  EAST: "market.structure.easternConference",
  WEST: "market.structure.westernConference",
  AFC: "market.structure.afc",
  NFC: "market.structure.nfc",
  AL_EAST: "market.structure.americanLeagueEast",
  AL_CENTRAL: "market.structure.americanLeagueCentral",
  AL_WEST: "market.structure.americanLeagueWest",
  NL_EAST: "market.structure.nationalLeagueEast",
  NL_CENTRAL: "market.structure.nationalLeagueCentral",
  NL_WEST: "market.structure.nationalLeagueWest",
  ATLANTIC: "market.structure.atlanticDivision",
  METROPOLITAN: "market.structure.metropolitanDivision",
  CENTRAL: "market.structure.centralDivision",
  PACIFIC: "market.structure.pacificDivision",
  AFC_EAST: "market.structure.afcEast",
  AFC_NORTH: "market.structure.afcNorth",
  AFC_SOUTH: "market.structure.afcSouth",
  AFC_WEST: "market.structure.afcWest",
  NFC_EAST: "market.structure.nfcEast",
  NFC_NORTH: "market.structure.nfcNorth",
  NFC_SOUTH: "market.structure.nfcSouth",
  NFC_WEST: "market.structure.nfcWest",
} as const;

export type StructureLabelKey = (typeof STRUCTURE_LABEL_KEYS)[keyof typeof STRUCTURE_LABEL_KEYS];

export function structureLabelKey(code: string): StructureLabelKey | null {
  const normalized = code.trim().toUpperCase();
  return STRUCTURE_LABEL_KEYS[normalized as keyof typeof STRUCTURE_LABEL_KEYS] ?? null;
}

export function displayStructureCode(code: string): string {
  return code.replace(/_/g, " ");
}
